import React, { useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { ClipLoader } from 'react-spinners';
import { CountryCode, PaymentType, ShippingAddress, SubscriptionService } from '@dayetopia/types';
import { useRecurly } from '@recurly/react-recurly';
import { styled } from 'styled-components';
import { Context } from '@components/Context';
import ApplePayButton from '@components/shared/ApplePayButton';
import ErrorMessage from '@components/shared/ErrorMessage';
import { MEDIA } from '@constants';
import { CartItem } from '@contracts';
import useTranslate from '@hooks/useTranslate';
import { signInAsGuest } from '@utils/account';
import { getDeliveryValidationSchema } from '@utils/addressValidation';
import { getApplePayLineItems, getGooglePayLineItems } from '@utils/cart';
import { checkProductsEligible } from '@utils/checkout';
import { isDevelopmentEnvironment } from '@utils/environment';
import { convertApplePayAddressToRecurly, convertGooglePayAddressToRecurly } from '@utils/helpers';
import { calculateShippingCost } from '@utils/shippingCost';
import { trackEvent } from '@utils/tracking';
import useCheckout from '@utils/useCheckout';
import Colors from './Colors';
import { useExpressCheckoutError } from './ExpressCheckoutErrorContext';

interface Props {
  cartItems: CartItem[];
  price: number;
  coupon?: string;
}

interface PaymentHandlerParams {
  tokenId: string;
  shippingAddress: ShippingAddress;
  email: string;
  paymentType: PaymentType;
}

const ExpressCheckoutButtons: React.FC<Props> = ({ cartItems, price, coupon = '' }) => {
  const { t } = useTranslate();
  const { state, dispatch } = useContext(Context);
  const { currency, country, firebaseAuth, freeShippingRequirement, shippingFees, tax } = state;
  const googlePayButtonRef = useRef<HTMLDivElement>(null);
  const [applePayReady, setApplePayReady] = useState(false);
  const [googlePayReady, setGooglePayReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const { errorMessage, setErrorMessage } = useExpressCheckoutError();
  const subtotal = price - (tax || 0);
  const shippingCost = calculateShippingCost({ price, freeShippingRequirement, cartItems, shippingFees });
  const totalPrice = subtotal + shippingCost.shippingPrice;
  const applePayTotal = { label: 'Daye', amount: totalPrice.toFixed(2) };
  const recurly = useRecurly();

  const handleError = useCallback((err: any): void => {
    console.error('Checkout error:', err);
    trackEvent('payment_error_gpay');
    setLoading(false);
    const apiErrorMessage = err.error || err.message || 'An error occurred during checkout. Please try again.';
    setErrorMessage(apiErrorMessage);
  }, []);

  const validateAddress = async (address: ShippingAddress): Promise<string | null> => {
    try {
      const addressValidationSchema = getDeliveryValidationSchema(t as (key: string, params?: object) => string);
      await addressValidationSchema.validate(address);
      return null;
    } catch (error: unknown) {
      if (error instanceof Error) {
        return error.message;
      }
      return t('address-error.invalid-address');
    }
  };

  const handleAddressValidation = useCallback(
    async (deliveryAddressObject: ShippingAddress): Promise<boolean> => {
      const addressError = await validateAddress(deliveryAddressObject);
      if (addressError) {
        handleError({ message: addressError });
        return false;
      }
      return true;
    },
    [handleError]
  );

  const handleCartItemsAdd = useCallback(() => {
    dispatch({ type: 'cartClear' });
    cartItems.forEach((cartItem) => {
      dispatch({
        type: 'cartItemAdd',
        data: cartItem
      });
    });
  }, [cartItems, dispatch]);

  const validateProductsEligibility = (deliveryCountry: CountryCode): boolean => {
    return checkProductsEligible(cartItems, deliveryCountry, setErrorMessage, undefined, t);
  };

  const { handleCheckout } = useCheckout({
    setLoading,
    setErrorMessage,
    setErrorList: () => {},
    handle3DS: () => null
  });

  // Shared payment handler function
  const handlePaymentProcessing = useCallback(
    async ({ tokenId, shippingAddress, email, paymentType }: PaymentHandlerParams): Promise<void> => {
      try {
        if (!firebaseAuth?.currentUser) {
          await signInAsGuest(firebaseAuth);
        }

        if (!(await handleAddressValidation(shippingAddress))) {
          return;
        }

        if (!validateProductsEligibility(shippingAddress.country as CountryCode)) {
          return;
        }

        await handleCheckout({
          tokenId,
          coupon,
          subscriptionServiceUsed: SubscriptionService.Recurly,
          paymentType,
          shippingAddress,
          isPayPalOrderPreview: false,
          primaryPaymentMethod: false,
          expressCartItems: cartItems,
          email
        });
      } catch (err: any) {
        console.error(err);
        trackEvent(`payment_error_${paymentType === PaymentType.ApplePay ? 'applepay' : 'gpay'}`);
        setLoading(false);
      }
    },
    [firebaseAuth, handleAddressValidation, validateProductsEligibility, handleCheckout, coupon, cartItems]
  );

  const applePay = useMemo(() => {
    if (isDevelopmentEnvironment()) return null; // disable apple pay on development
    return recurly.ApplePay({
      country,
      currency: currency.letterCode,
      label: 'Daye',
      paymentRequest: {
        total: applePayTotal,
        lineItems: getApplePayLineItems(cartItems, shippingCost.shippingPrice, tax || 0),
        requiredShippingContactFields: ['postalAddress', 'name', 'phone', 'email']
      }
    });
  }, [price, currency.letterCode]);

  const googlePay = useMemo(
    () =>
      recurly.GooglePay({
        googleMerchantId: process.env.GOOGLE_MERCHANT_ID,
        billingAddressRequired: true,
        buttonOptions: {
          buttonType: 'plain',
          buttonSizeMode: 'fill'
        },
        paymentDataRequest: {
          shippingAddressRequired: true,
          shippingAddressParameters: {
            phoneNumberRequired: true
          },
          emailRequired: true,
          transactionInfo: {
            currencyCode: currency.letterCode,
            countryCode: country,
            totalPrice: totalPrice?.toFixed(2),
            totalPriceLabel: 'Total',
            totalPriceStatus: 'FINAL',
            displayItems: getGooglePayLineItems(cartItems, shippingCost.shippingPrice, tax || 0)
          }
        }
      }),
    [price, currency.letterCode]
  );

  useEffect(() => {
    if (!applePay) return undefined;

    const handleApplePayError = (err: any): void => {
      console.error('Apple Pay error', err);
    };

    const handleApplePayCancel = (): void => {
      setLoading(false);
    };

    const handleApplePayAuthorized = (event: any): void => {
      console.debug('Apple Pay succeeded.', event);
      setLoading(true);
      handleCartItemsAdd();
      const { recurlyToken: token, shippingContact } = event.payment;
      const deliveryAddressObject = convertApplePayAddressToRecurly(shippingContact);

      handlePaymentProcessing({
        tokenId: token.id,
        shippingAddress: deliveryAddressObject,
        email: shippingContact.emailAddress,
        paymentType: PaymentType.ApplePay
      });
    };

    applePay.ready(() => {
      setApplePayReady(true);
    });

    applePay.on('error', handleApplePayError);
    applePay.on('cancel', handleApplePayCancel);
    applePay.on('paymentAuthorized', handleApplePayAuthorized);

    return function cleanup(): void {
      applePay.off('error', handleApplePayError);
      applePay.off('cancel', handleApplePayCancel);
      applePay.off('paymentAuthorized', handleApplePayAuthorized);
    };
  }, [applePay, handleCartItemsAdd, handlePaymentProcessing]);

  const handleApplePayClick = useCallback(() => {
    if (!applePay) return;
    applePay.begin();
  }, [applePay]);

  useEffect(() => {
    if (!googlePay) return undefined;

    const handleGooglePayError = (err: any): void => {
      handleError(err);
    };

    const handlePaymentAuthorized = async (event: any): Promise<void> => {
      console.debug('Google Pay succeeded.', event);
      setLoading(true);
      handleCartItemsAdd();
      const { recurlyToken: token, shippingAddress, email } = event;
      const deliveryAddressObject = convertGooglePayAddressToRecurly(shippingAddress);

      handlePaymentProcessing({
        tokenId: token.id,
        shippingAddress: deliveryAddressObject,
        email,
        paymentType: PaymentType.GooglePay
      });
    };

    const handleGooglePayReady = (googlePayButton: HTMLElement): void => {
      setGooglePayReady(true);
      const container = googlePayButtonRef.current;
      if (container) {
        container.innerHTML = '';
        container.appendChild(googlePayButton);
      }
    };

    googlePay.on('ready', handleGooglePayReady);
    googlePay.on('error', handleGooglePayError);
    googlePay.on('paymentAuthorized', handlePaymentAuthorized);

    return function cleanup(): void {
      googlePay.off('ready', handleGooglePayReady);
      googlePay.off('error', handleGooglePayError);
      googlePay.off('paymentAuthorized', handlePaymentAuthorized);
    };
  }, [googlePay, googlePayButtonRef, handleError, handleCartItemsAdd, handlePaymentProcessing]);

  const errorMessageComponent = useMemo(() => {
    if (!errorMessage) return null;
    return (
      <ErrorMessageWrapper>
        <ErrorMessage errorMessage={errorMessage} style={{ marginBottom: 0 }} />
      </ErrorMessageWrapper>
    );
  }, [errorMessage]);

  return (
    <>
      {loading ? (
        <LoadingWrap>
          <ClipLoader color={Colors.navyBlue} size={20} speedMultiplier={0.5} />
        </LoadingWrap>
      ) : null}
      <Container>
        {applePayReady && <ApplePayButton onClick={handleApplePayClick} $show={applePayReady} />}
        <GooglePayButtonContainer ref={googlePayButtonRef} $show={googlePayReady} />
      </Container>
      {errorMessageComponent}
    </>
  );
};

export default ExpressCheckoutButtons;

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: row;
  gap: 16px;

  & > div {
    flex: 1;
  }

  ${MEDIA.tablet} {
    flex-direction: column;
  }
`;

const GooglePayButtonContainer = styled.div<{ $show: boolean }>`
  height: 40px;
  width: 100%;
  display: ${(props) => (props.$show ? 'flex' : 'none')};

  & .gpay-button {
    background-repeat: no-repeat;
    background-position: center;
    border-radius: 2px;

    .gpay-card-info-animation-container {
      border-radius: 2px;
    }
  }

  .gpay-card-info-container {
    border-radius: 2px;

    ${MEDIA.tablet} {
      min-width: 100%;
    }
  }
`;

const LoadingWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ErrorMessageWrapper = styled.div`
  width: 100%;
`;
